import { useState, useEffect, useCallback, useRef } from 'react';
import { getNotifications } from '../services/notificationsApi';

export function useNotifications(params = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const paramsRef = useRef(params);
  const pollIntervalMs = Number(params?.pollIntervalMs ?? 0);
  const enabled = params?.enabled !== false;

  useEffect(() => {
    const paramsStr = JSON.stringify(params);
    const prevParamsStr = JSON.stringify(paramsRef.current);
    if (paramsStr !== prevParamsStr) {
      paramsRef.current = params;
    }
  }, [params]);

  const fetchData = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!mountedRef.current || !enabled) return;
    const currentParams = paramsRef.current;
    const requestParams = { ...currentParams };
    delete requestParams.pollIntervalMs;
    delete requestParams.enabled;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const list = await getNotifications(requestParams);
      if (!mountedRef.current) return;
      setData(Array.isArray(list) ? list : []);
    } catch (err) {
      if (!mountedRef.current) return;
      if (!silent) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setData([]);
      }
    } finally {
      if (mountedRef.current && !silent) {
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) fetchData({ silent: false });
    return () => {
      mountedRef.current = false;
    };
  }, [fetchData, enabled]);

  useEffect(() => {
    if (mountedRef.current && enabled) {
      fetchData({ silent: false });
    }
  }, [JSON.stringify(params), enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !Number.isFinite(pollIntervalMs) || pollIntervalMs <= 0) return undefined;
    const timer = setInterval(() => {
      if (mountedRef.current) fetchData({ silent: true });
    }, pollIntervalMs);
    return () => clearInterval(timer);
  }, [enabled, pollIntervalMs, fetchData]);

  return { data, loading, error, refetch: () => fetchData({ silent: true }) };
}
